// admin-client/src/pages/NoShows.jsx
// =============================================================================
// Per-crew no-show report for a month, ranked by most absences.
//
// "No-show" = the booked pilot did not sign in for a booked block. Each absence
// is split into "covered" (someone else used the IPT — shown with who) or
// "unused" (the IPT sat empty). Sourced from the month's latest cross-check
// run, so the numbers match the Cross-check page; if no run exists the page
// prompts to run one.
// =============================================================================

import { Fragment, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { fmtDate, fmtDateTime } from '../lib/datetime.js';

const TODAY = new Date();
const PREV = new Date(TODAY.getFullYear(), TODAY.getMonth() - 1, 1);
const DEFAULT_MONTH = `${PREV.getFullYear()}-${String(PREV.getMonth() + 1).padStart(2, '0')}`;

const hourRange = (start, hours) => {
  const end = start + hours;
  const fmt = (h) => `${String(h % 24).padStart(2, '0')}:00`;
  return `${fmt(start)}–${fmt(end)}${end > 24 ? ' (+1d)' : ''}`;
};

export default function NoShows() {
  const [month, setMonth] = useState(DEFAULT_MONTH);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [openRow, setOpenRow] = useState(null);

  const load = async () => {
    if (!/^\d{4}-\d{2}$/.test(month)) { setErr('Month must be YYYY-MM'); return; }
    setBusy(true); setErr(null); setOpenRow(null);
    try {
      setData(await api.crossCheck.noShowReport(month));
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const rows = useMemo(() => {
    const lb = data?.leaderboard || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return lb;
    return lb.filter(
      (u) =>
        u.cathay_id.toLowerCase().includes(needle) ||
        (u.display_name || '').toLowerCase().includes(needle)
    );
  }, [data, q]);

  const runCrossCheck = async () => {
    setBusy(true); setErr(null);
    try {
      await api.crossCheck.run({ month, includeSimulated: true });
      await load();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-cathay-700">No-shows</h1>
        <p className="text-sm text-slate-600 mt-1">
          Crew ranked by booked sessions they didn't sign in for. Each absence shows whether
          another crew member used the IPT (covered) or it sat empty (unused). Based on the
          month's latest cross-check.
        </p>
      </header>

      <section className="bg-white rounded shadow-sm border p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <div className="text-xs text-slate-600">Month</div>
            <input
              pattern="\d{4}-\d{2}" value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-1 border border-slate-300 rounded px-2 py-1.5 font-mono w-28"
            />
          </label>
          <button
            onClick={load} disabled={busy}
            className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-2 rounded text-sm"
          >
            {busy ? 'Loading…' : 'Load'}
          </button>
          <div className="flex-1" />
          <label className="text-sm">
            <div className="text-xs text-slate-600">Search crew</div>
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="CathayID or name"
              className="mt-1 border border-slate-300 rounded px-2 py-1.5 w-52"
            />
          </label>
        </div>

        {err && <div className="mt-3 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

        {data?.needs_run && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-800">
            No cross-check has been run for {data.month} yet, so there's nothing to report.
            <button
              onClick={runCrossCheck} disabled={busy}
              className="ml-3 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white px-3 py-1.5 rounded text-xs"
            >
              Run cross-check for {data.month}
            </button>
          </div>
        )}

        {data && !data.needs_run && (
          <div className="mt-2 text-xs text-slate-500">
            Latest cross-check for {data.month} ran {data.ran_at ? fmtDateTime(data.ran_at) : '—'}.
            {rows.length} crew with at least one no-show.
          </div>
        )}
      </section>

      {data && !data.needs_run && (
        <section className="bg-white rounded shadow-sm border overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr className="text-left">
                <th className="p-2 w-8">#</th>
                <th className="p-2">Crew</th>
                <th className="p-2 text-right">No-shows</th>
                <th className="p-2 text-right">Covered</th>
                <th className="p-2 text-right">Unused</th>
                <th className="p-2 text-right">Hours</th>
                <th className="p-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="p-4 text-center text-slate-500">No no-shows.</td></tr>
              )}
              {rows.map((u, i) => {
                const open = openRow === u.cathay_id;
                return (
                  <Fragment key={u.cathay_id}>
                    <tr
                      onClick={() => setOpenRow(open ? null : u.cathay_id)}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    >
                      <td className="p-2 text-slate-400">{i + 1}</td>
                      <td className="p-2">
                        <span className="font-mono font-medium">{u.cathay_id}</span>
                        {u.display_name && <span className="text-slate-500"> · {u.display_name}</span>}
                      </td>
                      <td className="p-2 text-right font-semibold text-red-700">{u.absences}</td>
                      <td className="p-2 text-right text-amber-700">{u.covered}</td>
                      <td className="p-2 text-right text-slate-600">{u.unused}</td>
                      <td className="p-2 text-right text-slate-600">{u.absent_hours}</td>
                      <td className="p-2 text-slate-400">{open ? '▾' : '▸'}</td>
                    </tr>
                    {open && (
                      <tr className="bg-slate-50/60">
                        <td></td>
                        <td colSpan={6} className="p-2">
                          <div className="border border-slate-200 rounded divide-y divide-slate-100 bg-white">
                            {u.details.map((d, j) => (
                              <div key={j} className="px-3 py-2 flex items-center gap-3 text-xs">
                                <div className="w-40 shrink-0 font-mono text-slate-600">
                                  {fmtDate(d.date)} {hourRange(d.start_hour, d.hours)}
                                </div>
                                <div className="w-32 shrink-0 text-slate-600">{d.device_name || '—'}</div>
                                {d.status === 'covered' ? (
                                  <div className="flex-1">
                                    <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                                      covered
                                    </span>{' '}
                                    <span className="text-slate-700">
                                      used by{' '}
                                      {d.covered_by.map((c, k) => (
                                        <span key={c.cathay_id}>
                                          {k > 0 && ', '}
                                          <span className="font-mono">{c.cathay_id}</span>
                                          <span className="text-slate-400"> ({c.hours}h)</span>
                                        </span>
                                      ))}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="flex-1">
                                    <span className="text-[10px] uppercase tracking-wide bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                                      unused
                                    </span>{' '}
                                    <span className="text-slate-500">IPT sat empty</span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
